import type { CreateAgenticCartOrderResponse } from "@ishvaram/api-client";
import { getPaymentOrderStatus } from "@/lib/api/plans";
import type { SuccessState } from "./AgenticCartCheckout.types";

const KEY = "ishvaram-cart-confirmation-v1";
const ORDER_PARAM = "confirm_order";
const CART_PARAM = "confirm_cart";

export type CartConfirmation = {
  bundleId: string;
  order: Pick<CreateAgenticCartOrderResponse, "cart_id" | "razorpay_order_id">;
  language: "en" | "hi";
};

// Save only the provider order reference and public checkout metadata. Contact,
// birth inputs, payment signatures and report access tokens are never stored.
export function rememberCartConfirmation(value: CartConfirmation): void {
  try {
    window.localStorage.setItem(
      KEY,
      JSON.stringify({
        bundleId: value.bundleId,
        language: value.language,
        order: { cart_id: value.order.cart_id, razorpay_order_id: value.order.razorpay_order_id },
      })
    );
  } catch {
    // The URL marker also survives a reload when browser storage is unavailable.
  }
  const url = new URL(window.location.href);
  url.searchParams.set(ORDER_PARAM, value.order.razorpay_order_id);
  url.searchParams.set(CART_PARAM, value.order.cart_id);
  url.searchParams.set("bundle_id", value.bundleId);
  url.searchParams.set("lang", value.language);
  window.history.replaceState(null, "", url);
}

export function readCartConfirmation(bundleId: string): CartConfirmation | null {
  const url = new URL(window.location.href);
  const orderId = url.searchParams.get(ORDER_PARAM);
  const cartId = url.searchParams.get(CART_PARAM);
  if (url.searchParams.get("bundle_id") !== bundleId || !orderId || !cartId) {
    return readStoredConfirmation(bundleId);
  }
  return {
    bundleId,
    language: url.searchParams.get("lang") === "hi" ? "hi" : "en",
    order: {
      razorpay_order_id: orderId,
      cart_id: cartId,
    },
  };
}

function readStoredConfirmation(bundleId: string): CartConfirmation | null {
  try {
    const value = JSON.parse(window.localStorage.getItem(KEY) || "null");
    return isSavedConfirmation(value, bundleId) ? value : null;
  } catch {
    return null;
  }
}

function isSavedConfirmation(value: unknown, bundleId: string): value is CartConfirmation {
  if (!value || typeof value !== "object") return false;
  const saved = value as Partial<CartConfirmation>;
  return (
    saved.bundleId === bundleId &&
    typeof saved.order?.razorpay_order_id === "string" &&
    typeof saved.order?.cart_id === "string" &&
    (saved.language === "en" || saved.language === "hi")
  );
}

export function forgetCartConfirmation(): void {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    // The URL remains the fallback when storage is unavailable.
  }
  const url = new URL(window.location.href);
  url.searchParams.delete(ORDER_PARAM);
  url.searchParams.delete(CART_PARAM);
  window.history.replaceState(null, "", url);
}

export async function checkCartConfirmation(
  saved: CartConfirmation
): Promise<SuccessState | "refunded" | null> {
  try {
    const result = await getPaymentOrderStatus(saved.order.razorpay_order_id);
    if (result.order_id !== saved.order.razorpay_order_id) return null;
    if (result.status === "refunded") return "refunded";
    if (result.status !== "activated") return null;
    return {
      order: saved.order,
      result: {
        success: true,
        cart_id: saved.order.cart_id,
        bundle_id: saved.bundleId,
        message: result.message,
      },
    };
  } catch {
    return null;
  }
}
